import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Lock, User, Loader2 } from 'lucide-react';
import LanguageSelector from './LanguageSelector';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const returnUrl = location.state?.from || '/admin/panel';

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/api/admin/login`, {
        username,
        password
      });

      if (response.data.success) {
        sessionStorage.setItem('adminToken', response.data.token);
        sessionStorage.setItem('adminTokenExpires', response.data.expires);
        toast.success(t('common.success'));
        navigate(returnUrl);
      }
    } catch (error) {
      console.error('Error login admin:', error);
      toast.error(error.response?.data?.detail || t('adminLogin.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F1F7FD] flex flex-col">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/poollex-logo.png" alt="PoolLex.io" className="h-10 w-auto" />
            </div>
            <LanguageSelector variant="outline" />
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-lg">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900">
              {t('adminLogin.title')}
            </CardTitle>
            <p className="text-slate-600 text-sm mt-2">
              {t('adminLogin.subtitle')}
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">{t('adminLogin.username')}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="pl-10"
                    placeholder={t('adminLogin.username')}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t('adminLogin.password')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    placeholder={t('adminLogin.password')}
                    required
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary-hover"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('common.loading')}
                  </>
                ) : (
                  t('adminLogin.loginButton')
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Component to protect admin routes
export const RequireAdminAuth = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isVerifying, setIsVerifying] = useState(true);
  const [isAuth, setIsAuth] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      const token = sessionStorage.getItem('adminToken');
      const expires = sessionStorage.getItem('adminTokenExpires');

      if (!token) {
        setIsVerifying(false);
        navigate('/admin/login', { state: { from: location.pathname } });
        return;
      }

      if (expires) {
        const expiresDate = new Date(expires);
        if (new Date() > expiresDate) {
          sessionStorage.removeItem('adminToken');
          sessionStorage.removeItem('adminTokenExpires');
          setIsVerifying(false);
          navigate('/admin/login', { state: { from: location.pathname } });
          return;
        }
      }

      try {
        await axios.get(`${API}/api/admin/verify`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsAuth(true);
      } catch (error) {
        console.error('Invalid token:', error);
        sessionStorage.removeItem('adminToken');
        sessionStorage.removeItem('adminTokenExpires');
        navigate('/admin/login', { state: { from: location.pathname } });
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [navigate, location]);

  if (isVerifying) {
    return (
      <div className="min-h-screen bg-[#F1F7FD] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (!isAuth) {
    return null;
  }

  return children;
};

export default AdminLogin;
